import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[Supabase] Missing environment variables: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
  throw new Error('Missing Supabase environment variables. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch {
  console.error('[Supabase] Invalid VITE_SUPABASE_URL:', supabaseUrl);
  throw new Error(`Invalid Supabase URL: "${supabaseUrl}". Must be a valid URL like https://your-project-id.supabase.co`);
}

// Custom fetch with retry logic for transient network failures
const fetchWithRetry = async (url, options, retries = 2) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (err) {
      const isLastAttempt = attempt === retries;
      if (isLastAttempt) {
        // On final failure, log a clear diagnostic message
        console.warn(
          `[Supabase] Network request failed after ${retries + 1} attempts.\n` +
          `  URL: ${typeof url === 'string' ? url : url?.url}\n` +
          `  Error: ${err?.message}\n` +
          `  Check: Is your Supabase project active? Is VITE_SUPABASE_URL correct?`
        );
        throw err;
      }
      // Wait before retrying (exponential backoff: 300ms, 600ms)
      await new Promise(resolve => setTimeout(resolve, 300 * Math.pow(2, attempt)));
    }
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'ozymakers-auth-token',
    lock: (_name, _acquireTimeout, fn) => fn(),
  },
  global: {
    fetch: fetchWithRetry,
  },
});

// Handle token refresh failures gracefully — clear broken session
supabase?.auth?.onAuthStateChange((event) => {
  if (event === 'TOKEN_REFRESH_FAILED') {
    console.warn('[Supabase] Token refresh failed — clearing local session.');
    supabase?.auth?.signOut({ scope: 'local' })?.catch(() => {});
  }
});

// Connection test — logs session state on startup to verify connectivity
supabase.auth.getSession()
  .then(({ data, error }) => {
    if (error) {
      console.warn('[Supabase] getSession error:', error?.message);
    } else {
      console.log('[Supabase] Connection OK. Session:', data?.session ? 'Active' : 'None');
    }
  })
  .catch((err) => {
    console.error('[Supabase] getSession fetch failed:', err?.message);
    console.error('[Supabase] Verify VITE_SUPABASE_URL points to an active (non-paused) project.');
  });
